import { useEffect, useRef, useState } from 'react';
import { GameEngine } from './game/engine.js';
import { gameSession } from './lib/api.js';
import { useGameStore } from './game/store.js';
import { TOUCH_MODE } from './game/touch.js';
import { ChatPanel } from './ui/chat.jsx';
import { GameOver } from './ui/gameover.jsx';
import { Hud, type HudHandles } from './ui/hud.jsx';
import { HubOverlay } from './ui/hub.jsx';
import { Lobby } from './ui/lobby.jsx';
import { Menu } from './ui/menu.jsx';
import { PauseMenu } from './ui/pause.jsx';
import { SettingsPanel } from './ui/settings.jsx';
import { RotateHint, TouchHud } from './ui/touch.jsx';
import { VoiceButton } from './ui/voice.jsx';

/** Briefly toggles a `data-show` attribute the CSS animates. */
const pulse = (element: HTMLElement | null, ms: number, ...extras: string[]): void => {
  if (!element) return;
  element.dataset.show = 'true';
  for (const extra of extras) element.dataset[extra] = 'true';
  window.setTimeout(() => {
    delete element.dataset.show;
    for (const extra of extras) delete element.dataset[extra];
  }, ms);
};

export const App = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<GameEngine | null>(null);
  const hudHandles = useRef<HudHandles>({ hitMarker: null, damageFlash: null });

  const phase = useGameStore((state) => state.phase);
  const matchPhase = useGameStore((state) => state.matchPhase);
  const inHub = useGameStore((state) => state.inHub);
  const pointerLocked = useGameStore((state) => state.pointerLocked);
  const setScoreboardOpen = useGameStore((state) => state.setScoreboardOpen);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Touch has no pointer lock, so "paused" is an explicit button rather than
  // a side effect of releasing the cursor.
  const [touchPaused, setTouchPaused] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const engine = new GameEngine(canvas, {
      onHitMarker: (killed, headshot) =>
        pulse(
          hudHandles.current.hitMarker,
          killed ? 260 : headshot ? 180 : 120,
          ...(killed ? ['kill'] : []),
          ...(headshot ? ['head'] : []),
        ),
      onDamageTaken: () => pulse(hudHandles.current.damageFlash, 90),
    });
    engineRef.current = engine;
    engine.start();

    return () => {
      engine.dispose();
      engineRef.current = null;
    };
  }, []);

  // Scoreboard is held, not toggled — matching the genre convention.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code !== 'Tab') return;
      event.preventDefault();
      setScoreboardOpen(true);
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code === 'Tab') setScoreboardOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [setScoreboardOpen]);

  const connected = phase === 'playing';
  // The world keeps rendering behind every overlay — the walkable lobby, the
  // loadout screen and the pause menu are all layers over the arena, never a
  // loading screen. `roundEnd` is still "in the round": the banner floats over
  // the frozen arena and the next round starts by itself.
  const inRound =
    connected && !inHub && (matchPhase === 'live' || matchPhase === 'roundEnd');
  const gameOver = connected && !inHub && matchPhase === 'ended';
  const walkable = inRound || (connected && inHub);
  // On touch, pausing is an explicit button; on desktop it is losing the cursor.
  const paused = TOUCH_MODE ? touchPaused : !pointerLocked;
  const resume = () => {
    setTouchPaused(false);
    if (!TOUCH_MODE) engineRef.current?.requestPointerLock();
  };

  return (
    <>
      <canvas
        ref={canvasRef}
        className="block size-full"
        onClick={() => walkable && !TOUCH_MODE && engineRef.current?.requestPointerLock()}
      />
      {TOUCH_MODE && connected && <RotateHint />}
      {connected && <ChatPanel onSend={(text) => engineRef.current?.sendChat(text)} />}
      {connected && (
        <VoiceButton onToggle={() => void engineRef.current?.toggleMic()} />
      )}
      {TOUCH_MODE && walkable && !paused && (
        <TouchHud
          onButton={(bit, held) => engineRef.current?.setTouchButton(bit, held)}
          onCycleWeapon={() => engineRef.current?.cycleWeapon()}
          onPause={() => setTouchPaused(true)}
        />
      )}
      {connected ? (
        inHub ? (
          <>
            <HubOverlay
              onVote={(mapIndex) => engineRef.current?.vote(mapIndex)}
              onVoteDifficulty={(tier) => engineRef.current?.voteDifficulty(tier)}
              onReady={() => engineRef.current?.padReady()}
            />
            {paused && (
              <PauseMenu
                onResume={resume}
                onSettings={() => setSettingsOpen(true)}
                onLeave={() => {
                  setTouchPaused(false);
                  engineRef.current?.leave();
                }}
              />
            )}
          </>
        ) : inRound ? (
          <>
            <Hud handles={hudHandles} />
            {paused && matchPhase === 'live' && (
              <PauseMenu
                onResume={resume}
                onSettings={() => setSettingsOpen(true)}
                onLeave={() => {
                  setTouchPaused(false);
                  engineRef.current?.leaveMatch();
                }}
              />
            )}
          </>
        ) : gameOver ? (
          <GameOver
            onRematch={() => engineRef.current?.rematch()}
            onLeave={() => engineRef.current?.leaveMatch()}
          />
        ) : (
          <Lobby
            onSelect={(weapon) => engineRef.current?.selectLoadout(weapon)}
            onSettings={() => setSettingsOpen(true)}
            onLeave={() => engineRef.current?.leaveMatch()}
          />
        )
      ) : (
        <Menu
          onPlay={(name, character) => {
            // Android can go fullscreen off this tap; iOS ignores the request.
            if (TOUCH_MODE) {
              document.documentElement.requestFullscreen?.().catch(() => {});
            }
            engineRef.current?.join(name, character, undefined, gameSession());
          }}
          onSettings={() => setSettingsOpen(true)}
        />
      )}

      {settingsOpen && (
        <SettingsPanel
          onClose={() => setSettingsOpen(false)}
          onPreviewAudio={() => {
            // Needs the click as its gesture if the game has not started yet.
            engineRef.current?.audio.resume();
            engineRef.current?.audio.uiSelect();
          }}
        />
      )}
    </>
  );
};
